import { AuthContext } from "../context/auth";
import { useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { ROUTES } from "../constants/routes";
import Loader from "../components/Loader";
import { RoleTypes } from "../enums/roles.enum";
const AuthGuard = ({
    roles,
    userTypes,
    path,
    organizationType,
    children,
}: any) => {
    const navigate = useNavigate();
    const { authState } = useContext(AuthContext);

    const isAuthenticated = useMemo(() => {
        return !authState?.loading && authState?.user && authState?.token;
    }, [authState?.loading, authState?.user, authState?.token]);

    useEffect(() => {
        if (!authState?.loading && isAuthenticated && authState?.user) {
            const userType = authState?.user?.attributes?.type;
            const userRole =
                authState?.user?.relationships?.role?.attributes?.name;
            const isPermittedRole = roles?.includes(userRole);
            const isPermitedTypes = userTypes?.includes(userType);
            const features =
                authState?.user?.relationships?.organization?.relationships
                    ?.features?.attributes;
            const settings =
                authState?.user?.relationships?.organization?.relationships
                    ?.tenant_settings?.attributes;
            const isSandbox = Boolean(
                authState?.user?.relationships?.organization?.attributes
                    ?.is_sandbox
            );

            const hasBothTypesAndRoles =
                Boolean(roles?.length > 0) && Boolean(userTypes?.length > 0);

            const hasOrganizationType =
                !organizationType ||
                organizationType.includes(
                    authState?.user?.relationships?.organization?.attributes
                        ?.type
                );

            if (
                userType === RoleTypes.user &&
                authState?.user?.relationships?.role?.attributes?.name !==
                "Budtender"
            ) {
                if (!features) {
                    return;
                }

                if (
                    path === ROUTES.USER.geofences.key &&
                    !settings?.is_geofencing_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.games.key,
                        ROUTES.USER.games_create.key,
                        ROUTES.USER.games_edit.key,
                        ROUTES.USER.game_events.key,
                        ROUTES.USER.game_events_create.key,
                        ROUTES.USER.game_events_edit.key,
                        ROUTES.USER.game_entries.key,
                        ROUTES.USER.game_entries_create.key,
                    ].includes(path) &&
                    !features?.is_games_enabled &&
                    !features?.is_daily_rewards_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    path === ROUTES.USER.orders.key &&
                    !features.is_orders_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.aiq_flow_events.key,
                        ROUTES.USER.aiq_flow_events_create.key,
                    ].includes(path) &&
                    !isSandbox
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.achievements.key,
                        ROUTES.USER.achievements_create.key,
                        ROUTES.USER.single_achievements_create.key,
                        ROUTES.USER.single_achievements_edit.key,
                    ].includes(path) &&
                    !features.is_achievements_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.tiered_achievements_create.key,
                        ROUTES.USER.tiered_achievements_edit.key,
                    ].includes(path) &&
                    !features.is_tiered_achievements_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.coupons.key,
                        ROUTES.USER.coupons_create.key,
                        ROUTES.USER.coupons_edit.key,
                    ].includes(path) &&
                    !features.is_coupons_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.rewards.key,
                        ROUTES.USER.rewards_edit.key,
                        ROUTES.USER.reward_categories.key,
                    ].includes(path) &&
                    !features.is_rewards_enabled &&
                    !features.is_custom_rewards_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [ROUTES.USER.rewards_create.key].includes(path) &&
                    !features.is_custom_rewards_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [ROUTES.USER.rewards_create.key].includes(path) &&
                    !features.is_custom_rewards_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.punchcards.key,
                        ROUTES.USER.punchcards_create.key,
                        ROUTES.USER.punchcards_edit.key,
                        ROUTES.USER.punchcards_dashboard.key,
                    ].includes(path) &&
                    !features.is_punchcards_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.reports.key,
                        ROUTES.USER.referral_report.key,
                    ].includes(path) &&
                    !features.is_referrals_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                // if (
                //     [
                //         ROUTES.USER.daily_rewards.key,
                //         ROUTES.USER.daily_rewards_create.key,
                //         ROUTES.USER.daily_rewards_edit.key,
                //     ].includes(path) &&
                //     !features.is_daily_rewards_enabled
                // ) {
                //     navigate(ROUTES.USER.overview.key);
                //     return;
                // }

                if (
                    [
                        ROUTES.USER.micro_currencies.key,
                        ROUTES.USER.micro_currencies_create.key,
                        ROUTES.USER.micro_currencies_edit.key,
                    ].includes(path) &&
                    !features.is_micro_currency_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.gift_card_settings.key,
                    ].includes(path) &&
                    !features.is_gift_card_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.tiers.key,
                        ROUTES.USER.tiers_create.key,
                        ROUTES.USER.tiers_edit.key,
                    ].includes(path) &&
                    !features.is_tiers_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.sweepstakes.key,
                        ROUTES.USER.sweepstakes_create.key,
                        ROUTES.USER.sweepstakes_edit.key,
                    ].includes(path) &&
                    !features.is_sweepstakes_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.brand_boosts.key,
                    ].includes(path) &&
                    !features.is_brand_boost_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [
                        ROUTES.USER.engagement.key,
                        ROUTES.USER.engagement_create.key,
                        ROUTES.USER.engagement_edit.key,
                        ROUTES.USER.engagement_detail.key,
                    ].includes(path) &&
                    !features.is_engagement_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    [ROUTES.USER.delivery_settings.key].includes(path) &&
                    !features.is_delivery_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }

                if (
                    path === ROUTES.USER.custom_content_haptics.key &&
                    !features.is_haptics_enabled
                ) {
                    navigate(ROUTES.USER.overview.key);
                    return;
                }
            }
            if (
                (hasBothTypesAndRoles &&
                    isPermittedRole &&
                    isPermitedTypes &&
                    hasOrganizationType) ||
                (!hasBothTypesAndRoles && (isPermittedRole || isPermitedTypes))
            ) {
                return;
            } else {
                if (userType === RoleTypes.admin) {
                    navigate(ROUTES.ADMIN.dashboard.key);
                    return;
                }
                if (userType === RoleTypes.user) {
                    if (
                        authState?.user?.relationships?.role?.attributes
                            ?.name === "Budtender"
                    ) {
                        navigate(ROUTES.USER.orders.key);
                        return;
                    } else {
                        navigate(ROUTES.USER.overview.key);
                        return;
                    }
                }
            }
        }
        if (!authState?.loading && !isAuthenticated) {
            navigate(ROUTES.AUTH.login.key);
            return;
        }
    }, [isAuthenticated]);

    return (
        <>
            {authState?.loading ? (
                <div className="flex justify-center">
                    <Loader />
                </div>
            ) : (
                <> {children}</>
            )}
        </>
    );
};

export default AuthGuard;
